import {
  useCart,
  useCartLine,
  CartLineQuantityAdjustButton,
  CartLinePrice,
  CartLineQuantity,
  Image,
} from '@shopify/hydrogen';
import type {Image as ImageType} from '@shopify/hydrogen/storefront-api-types';

import {IconRemove, InternalLink, Text} from '~/components';
import {translateProductOptionName} from '~/lib/utils';

export function CartLineItem({onClose}: {onClose?: () => void}) {
  const {linesRemove} = useCart();
  const {id: lineId, quantity, merchandise} = useCartLine();

  return (
    <li
      key={lineId}
      className="grid border-b pb-5 gap-4 grid-cols-2 md:grid-cols-4"
    >
      <div className="card-image aspect-square w-24 h-24 md:w-28 md:h-28">
        {merchandise.image && (
          <InternalLink
            documentType="product"
            slug={merchandise.product.handle}
            onClick={onClose}
          >
            <Image
              width={112}
              height={112}
              widths={[112]}
              data={merchandise.image as ImageType}
              loaderOptions={{
                scale: 2,
              }}
              alt={merchandise.image?.altText ?? merchandise.product.title}
              className="!p-0 z-20 object-fit object-center"
            />
          </InternalLink>
        )}
      </div>

      <div className="pr-4 w-full">
        <h3 className="text-xxs">
          <InternalLink
            documentType="product"
            slug={merchandise.product.handle}
            onClick={onClose}
          >
            {merchandise.product.title}
          </InternalLink>
        </h3>
        <div className="pb-2">
          {(merchandise?.selectedOptions || []).map((option) => (
            <CartLineItemOption option={option} key={option.name} />
          ))}
        </div>
      </div>
      <div className="flex items-baseline justify-start w-full">
        <div className="flex justify-start text-copy">
          <CartLineQuantityAdjust lineId={lineId} quantity={quantity} />
        </div>
        <button
          type="button"
          onClick={() => linesRemove([lineId])}
          className="ml-4 relative top-1 flex items-center justify-center w-6 h-6"
        >
          <span className="sr-only">Remove</span>
          <IconRemove aria-hidden="true" />
        </button>
      </div>
      <div className="text-right">
        <CartLinePrice className="tracking-wider" as="h5" />
        <CartLinePrice
          className="text-textTertiary line-through py-1"
          as="h5"
          priceType="compareAt"
        />
      </div>
    </li>
  );
}

function CartLineQuantityAdjust({
  lineId,
  quantity,
}: {
  lineId: string;
  quantity: number;
}) {
  return (
    <>
      <label htmlFor={`quantity-${lineId}`} className="sr-only">
        Quantity, {quantity}
      </label>
      <div className="flex items-center border border-black">
        <CartLineQuantityAdjustButton
          adjust="decrease"
          aria-label="Decrease quantity"
          className="w-10 h-10 transition border-black border-r text-primary/50 hover:text-primary disabled:cursor-wait"
        >
          &#8722;
        </CartLineQuantityAdjustButton>
        <CartLineQuantity as="div" className="px-4 text-center" />
        <CartLineQuantityAdjustButton
          adjust="increase"
          aria-label="Increase quantity"
          className="w-10 h-10 transition border-black border-l text-primary/50 hover:text-primary disabled:cursor-wait"
        >
          &#43;
        </CartLineQuantityAdjustButton>
      </div>
    </>
  );
}

// If product has no variants, we get an option like "Title: Default title"
function CartLineItemOption({option}: {option: {name: string; value: string}}) {
  if (option.name == 'Title') {
    return null;
  }
  return (
    <Text className="text-sm mr-2" color="subtle" key={option.name}>
      {translateProductOptionName(option.name)}: {option.value}
    </Text>
  );
}
